import type { CSSProperties } from 'react';

import type { Category } from '@/papermill/extraction';
import { formatClock } from '@/papermill/transcript';

import styles from './AudioPlayer.module.css';

export interface PlayerTurn {
  startMs: number;
  endMs: number;
  /** The speaker's colour slot. */
  slot: number;
}

export interface PlayerMarker {
  ms: number;
  category: Category;
  label: string;
}

export interface AudioPlayerProps {
  status: 'loading' | 'ready' | 'error';
  playing: boolean;
  currentMs: number;
  durationMs: number;
  onToggle: () => void;
  onSeek: (ms: number) => void;
  rate: number;
  onRate: (rate: number) => void;
  /** Who spoke when, drawn as bands on the timeline. */
  turns?: PlayerTurn[];
  /** Where values were heard, drawn as ticks above the bands. */
  markers?: PlayerMarker[];
}

const RATES = [1, 1.25, 1.5, 2];
const SKIP_MS = 10_000;

function PlayIcon({ playing }: { playing: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {playing ? (
        <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />
      ) : (
        <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" />
      )}
    </svg>
  );
}

function pct(ms: number, durationMs: number): string {
  return `${Math.min(100, Math.max(0, (ms / Math.max(1, durationMs)) * 100))}%`;
}

/** The recording's transport: play, skip, speed, and a timeline of who spoke when. */
export function AudioPlayer({
  status,
  playing,
  currentMs,
  durationMs,
  onToggle,
  onSeek,
  rate,
  onRate,
  turns = [],
  markers = [],
}: AudioPlayerProps) {
  const disabled = status !== 'ready';
  const nextRate = RATES[(RATES.indexOf(rate) + 1) % RATES.length] ?? 1;

  return (
    <div className={styles.player} data-status={status}>
      <button
        type="button"
        className={styles.play}
        onClick={onToggle}
        disabled={disabled}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {status === 'loading' ? (
          <span className={styles.spinner} aria-hidden="true" />
        ) : (
          <PlayIcon playing={playing} />
        )}
      </button>

      <div className={styles.body}>
        <div className={styles.timeline}>
          <div className={styles.markers} aria-hidden="true">
            {markers.map((marker, index) => (
              <span
                key={`${marker.ms}-${index}`}
                className={styles.marker}
                title={marker.label}
                style={
                  {
                    left: pct(marker.ms, durationMs),
                    '--marker': `var(--series-${marker.category})`,
                  } as CSSProperties
                }
              />
            ))}
          </div>
          <div className={styles.track} aria-hidden="true">
            {turns.map((turn, index) => (
              <span
                key={`${turn.startMs}-${index}`}
                className={styles.turn}
                style={
                  {
                    left: pct(turn.startMs, durationMs),
                    width: pct(turn.endMs - turn.startMs, durationMs),
                    '--speaker': `var(--series-${turn.slot})`,
                  } as CSSProperties
                }
              />
            ))}
            <span className={styles.played} style={{ width: pct(currentMs, durationMs) }} />
            <span className={styles.head} style={{ left: pct(currentMs, durationMs) }} />
          </div>
          <input
            className={styles.range}
            type="range"
            aria-label="Seek"
            aria-valuetext={`${formatClock(currentMs)} of ${formatClock(durationMs)}`}
            min={0}
            max={Math.max(1, durationMs)}
            step={100}
            value={Math.min(currentMs, durationMs)}
            disabled={disabled}
            onChange={(event) => onSeek(Number(event.target.value))}
          />
        </div>
        <div className={styles.controls}>
          <span className={styles.time}>
            {formatClock(currentMs)}{' '}
            <span className={styles.total}>/ {formatClock(durationMs)}</span>
          </span>
          <span className={styles.buttons}>
            <button
              type="button"
              className={styles.small}
              aria-label="Back 10 seconds"
              disabled={disabled}
              onClick={() => onSeek(Math.max(0, currentMs - SKIP_MS))}
            >
              −10s
            </button>
            <button
              type="button"
              className={styles.small}
              aria-label="Forward 10 seconds"
              disabled={disabled}
              onClick={() => onSeek(Math.min(durationMs, currentMs + SKIP_MS))}
            >
              +10s
            </button>
            <button
              type="button"
              className={styles.small}
              aria-label={`Playback speed ${rate}×, change to ${nextRate}×`}
              disabled={disabled}
              onClick={() => onRate(nextRate)}
            >
              {rate}×
            </button>
          </span>
        </div>
        {status === 'error' ? (
          <p className={styles.error} role="alert">
            The recording could not be loaded. The transcript is still here to read.
          </p>
        ) : null}
      </div>
    </div>
  );
}
